import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { minorShadow } from './figure-shadows.js';

/**
 * Folds a finished creature's rigid parts and separate skinned surfaces into one skinned mesh per material, bound to one skeleton whose
 * bones are the original bones plus every group a rigid part hangs from. A part follows its group exactly as before (a group's
 * animation, a jaw opening, a paw flexing), so this needs only that no folded part's own transform changes after it, and it turns a
 * creature built of many small pieces (a war rat had ~35 draws) into a handful. Parts in `keep`, or below it, stay as they are.
 */
export function foldIntoSkin(root: THREE.Object3D, keep: Iterable<THREE.Object3D> = []) {
  root.updateMatrixWorld(true);
  const kept = new Set(keep), parts: THREE.Mesh[] = [];
  const foldable = (object: THREE.Object3D): object is THREE.Mesh => {
    if (!(object instanceof THREE.Mesh) || object instanceof THREE.InstancedMesh || Array.isArray(object.material) || Object.keys(object.geometry.morphAttributes).length) return false;
    if (object.onBeforeRender !== THREE.Object3D.prototype.onBeforeRender || object.customDepthMaterial || object.customDistanceMaterial || object.children.length) return false;
    if (object instanceof THREE.SkinnedMesh && (object.parent !== root || object.bindMode !== THREE.AttachedBindMode)) return false;
    for (let up: THREE.Object3D | null = object; up && up !== root; up = up.parent) if (kept.has(up) || !up.visible) return false;
    return true;
  };
  root.traverse(object => { if (foldable(object)) parts.push(object); });
  if (parts.length < 2) return;
  // One skeleton: every original bone keeps its own inverse; a group a rigid part hangs from is posed from where it is now.
  const bones: THREE.Object3D[] = [], inverses: THREE.Matrix4[] = [], index = new Map<THREE.Object3D, number>();
  const boneOf = (node: THREE.Object3D, inverse: () => THREE.Matrix4) => {
    let at = index.get(node);
    if (at === undefined) { at = bones.length; bones.push(node); inverses.push(inverse()); index.set(node, at); }
    return at;
  };
  const bind = root.matrixWorld.clone(), unbind = bind.clone().invert();
  // Parts that would cast no shadow on their own (`trimFigureShadows`) are folded apart from those that would, so shadows stay as they were.
  const groups = new Map<string, { material: THREE.Material; cast: boolean; receive: boolean; name: string; geometries: THREE.BufferGeometry[] }>();
  const normal = new THREE.Matrix3();
  for (const part of parts) {
    const material = part.material as THREE.Material, source = part.geometry, count = source.getAttribute('position').count;
    const geometry = new THREE.BufferGeometry(), toBind = new THREE.Matrix4(), skinIndex = new Uint16Array(count * 4), skinWeight = new Float32Array(count * 4);
    if (part instanceof THREE.SkinnedMesh) {
      // Its vertices already sit in its own bind space; move them to the shared one and renumber their bones.
      toBind.multiplyMatrices(unbind, part.bindMatrix);
      const indices = source.getAttribute('skinIndex'), weights = source.getAttribute('skinWeight');
      for (let v = 0; v < count; v++) for (let c = 0; c < 4; c++) {
        const weight = weights.getComponent(v, c); if (!weight) continue;
        const bone = indices.getComponent(v, c);
        skinIndex[v * 4 + c] = boneOf(part.skeleton.bones[bone], () => part.skeleton.boneInverses[bone].clone()); skinWeight[v * 4 + c] = weight;
      }
    } else {
      toBind.multiplyMatrices(unbind, part.matrixWorld);
      const parent = part.parent!, bone = boneOf(parent, () => parent.matrixWorld.clone().invert());
      for (let v = 0; v < count; v++) { skinIndex[v * 4] = bone; skinWeight[v * 4] = 1; }
    }
    geometry.setAttribute('position', source.getAttribute('position').clone().applyMatrix4(toBind));
    const normals = source.getAttribute('normal');
    geometry.setAttribute('normal', normals ? normals.clone().applyNormalMatrix(normal.getNormalMatrix(toBind)) : new THREE.Float32BufferAttribute(new Float32Array(count * 3), 3));
    const textured = !!((material as THREE.MeshStandardMaterial).map || (material as THREE.MeshStandardMaterial).bumpMap);
    if (textured) geometry.setAttribute('uv', source.getAttribute('uv')?.clone() ?? new THREE.Float32BufferAttribute(new Float32Array(count * 2), 2));
    if ((material as THREE.MeshStandardMaterial).vertexColors) {
      const color = source.getAttribute('color');
      geometry.setAttribute('color', color && color.itemSize === 3 ? color.clone() : new THREE.Float32BufferAttribute(new Float32Array(count * 3).fill(1), 3));
    }
    geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(skinIndex, 4)); geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(skinWeight, 4));
    geometry.setIndex(source.index ? Array.from(source.index.array) : Array.from({ length: count }, (_, i) => i));
    const cast = part.castShadow && !minorShadow(part), key = `${material.uuid}:${cast}:${part.receiveShadow}`;
    const group = groups.get(key) ?? { material, cast, receive: part.receiveShadow, name: part.name, geometries: [] };
    group.geometries.push(geometry); groups.set(key, group);
  }
  const skeleton = new THREE.Skeleton(bones as THREE.Bone[], inverses);
  for (const part of parts) {
    part.removeFromParent(); part.geometry.dispose();
    if (part instanceof THREE.SkinnedMesh && part.skeleton !== skeleton) part.skeleton.dispose();
  }
  for (const { material, cast, receive, name, geometries } of groups.values()) {
    const geometry = mergeGeometries(geometries)!;
    for (const temporary of geometries) temporary.dispose();
    const mesh = new THREE.SkinnedMesh(geometry, material);
    mesh.name = name; mesh.castShadow = cast; mesh.receiveShadow = receive;
    root.add(mesh); mesh.bind(skeleton, bind);
  }
}
